import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Users, Clock, ShieldCheck, ChartLineUp, PencilSimple } from '@phosphor-icons/react';
import { AssignEmployeesModal } from './AssignEmployeesModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtEUR = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} EUR`;

export const ProjectWorkforce = ({ projectId, projectName = '' }) => {
  const [data, setData] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);

  const fetchWF = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects/${projectId}/workforce`, { withCredentials: true });
      setData(res.data);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { if (projectId) fetchWF(); }, [projectId, fetchWF]);

  if (!data) return null;

  const { rows, totals } = data;
  const currentAssignedIds = rows.filter(r => r.is_assigned).map(r => r.user_id);

  return (
    <div className="card" style={{ marginBottom: '24px' }} data-testid="project-workforce">
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><Users size={18} /> Effectifs du chantier</h3>
        <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div><strong>{rows.length}</strong> salarie(s)</div>
          <div style={{ color: '#2E7D32' }}><Clock size={14} style={{ verticalAlign: 'middle' }} /> <strong>{totals.hours_approved}h</strong> validees</div>
          {totals.hours_pending > 0 && <div style={{ color: '#EF6C00' }}>+{totals.hours_pending}h en attente</div>}
          <div style={{ color: '#002FA7' }}>Cout MO : <strong>{fmtEUR(totals.labor_cost_approved)}</strong></div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setShowAssignModal(true)}
            data-testid="open-assign-employees-btn"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <PencilSimple size={14} weight="bold" /> Modifier l'effectif
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#5E5F66' }}>
          Aucun salarie affecte ni n'a encore pointe sur ce chantier.
          <div style={{ marginTop: '10px' }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowAssignModal(true)}
              data-testid="empty-assign-employees-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Users size={14} weight="bold" /> Affecter des salaries
            </button>
          </div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th>Salarie</th>
                <th>Poste</th>
                <th style={{ textAlign: 'right' }}>Taux journalier</th>
                <th style={{ textAlign: 'right' }}>Heures validees</th>
                <th style={{ textAlign: 'right' }}>Heures en attente</th>
                <th style={{ textAlign: 'right' }}>Cout MO valide</th>
                <th style={{ textAlign: 'right' }}>Cout projete</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.user_id} data-testid={`wf-row-${r.user_id}`}>
                  <td style={{ fontWeight: '600' }}>{r.name}</td>
                  <td style={{ color: '#5E5F66' }}>{r.job_title || '-'}</td>
                  <td style={{ textAlign: 'right' }}>{r.daily_rate ? `${r.daily_rate} EUR/j` : <span style={{ color: '#9E9E9E' }}>-</span>}</td>
                  <td style={{ textAlign: 'right', fontWeight: '600' }}>{r.hours_approved}h</td>
                  <td style={{ textAlign: 'right', color: r.hours_pending > 0 ? '#EF6C00' : '#9E9E9E' }}>{r.hours_pending > 0 ? `${r.hours_pending}h` : '-'}</td>
                  <td style={{ textAlign: 'right', color: '#2E7D32', fontWeight: '700' }}>{fmtEUR(r.labor_cost_approved)}</td>
                  <td style={{ textAlign: 'right', color: '#002FA7' }}>{fmtEUR(r.labor_cost_projected)}</td>
                  <td>
                    {r.is_assigned ? (
                      <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: '#E8F5E9', color: '#2E7D32', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <ShieldCheck size={12} /> Affecte
                      </span>
                    ) : (
                      <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: '#FFF3E0', color: '#EF6C00', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <ChartLineUp size={12} /> Ponctuel
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAssignModal && (
        <AssignEmployeesModal
          projectId={projectId}
          projectName={projectName}
          currentAssignedIds={currentAssignedIds}
          onClose={() => setShowAssignModal(false)}
          onSaved={fetchWF}
        />
      )}
    </div>
  );
};
